import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { Pagination } from '../models/pagination';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse } from '../models/api-response';
import { Product } from '../models/product';
import { HttpHeaders } from '@angular/common/http';
import { Manual } from '../models/manual';
import { List } from '../models/list';
import { TechnicalSheet } from '../models/technical-sheet';
import { TechnicalSheetEntry } from '../models/technical-sheet-entry';
import { VideoEntry } from '../models/video-entry';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  endpoint: string;

  constructor(private baseService: BaseService) {
    this.endpoint = 'products';
  }

  public getAll(pagination?: Pagination): Observable<ApiResponse<List<Product>>> {
    return this.baseService.getAll<List<Product>>(this.endpoint, { params: <{}>pagination })
    .pipe(map(data => {
      data.payload.elements.forEach(e => {
        if (!e.id) {
          e.id = e._id;
          delete e._id;
        }
        if (!!e.category && !e.category.id) {
          e.category.id = e.category._id;
          delete e.category._id;
        }
      });
      return data;
    }));
  }

  public get(id: string): Observable<ApiResponse<Product>> {
    return this.baseService.get<Product>(`${this.endpoint}/${id}`);
  }

  public add(product: Product): Observable<ApiResponse<Product>> {
    return this.baseService.add<Product>(this.endpoint, product);
  }

  public update(id: string, product: Product): Observable<ApiResponse<Product>> {
    return this.baseService.update<Product>(`${this.endpoint}/${id}`, product);
  }

  public delete(id: string): Observable<ApiResponse<Product>> {
    return this.baseService.delete<Product>(`${this.endpoint}/${id}`);
  }

  public show(id: string): Observable<ApiResponse<Product>> {
    return this.baseService.add<Product>(`${this.endpoint}/${id}/show`);
  }

  public hide(id: string): Observable<ApiResponse<Product>> {
    return this.baseService.add<Product>(`${this.endpoint}/${id}/hide`);
  }

  public showSection(id: string, section: string): Observable<ApiResponse<Product>> {
    return this.baseService.add<Product>(`${this.endpoint}/${id}/show/${section}`);
  }

  public hideSection(id: string, section: string): Observable<ApiResponse<Product>> {
    return this.baseService.add<Product>(`${this.endpoint}/${id}/hide/${section}`);
  }

  public updateImage(id: string, image: File): Observable<ApiResponse<Product>> {
    let formData: FormData = new FormData();
    formData.append('image', image, image.name);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');
    return this.baseService.add<Product>(`${this.endpoint}/${id}/image`, formData, headers);
  }

  public getManuals(id: string): Observable<ApiResponse<List<Manual>>> {
    return this.baseService.get<List<Manual>>(`${this.endpoint}/${id}/manuals`)
    .pipe(map(data => {
      data.payload.elements.forEach(e => {
        if (!e.id) {
          e.id = e._id;
          delete e._id;
        }
        if (!!e.contents) {
          e.contents.forEach(c => {
            if (!c.id) {
              c.id = c._id;
              delete c._id;
            }
            if (!!c.assets) {
              c.assets.forEach(a => {
                if (!a.id) {
                  a.id = a._id;
                  delete a._id;
                }
              });
            }
          });
        }
      });
      return data;
    }));
  }

  public getManual(id: string, manualId: string): Observable<ApiResponse<Manual>> {
    return this.baseService.get<Manual>(`${this.endpoint}/${id}/manuals/${manualId}`);
  }

  public addManual(id: string, manual: Manual): Observable<ApiResponse<Manual>> {
    return this.baseService.add<Manual>(`${this.endpoint}/${id}/manuals`, manual);
  }

  public updateManual(id: string, manualId: string, manual: Manual): Observable<ApiResponse<Manual>> {
    return this.baseService.update<Manual>(`${this.endpoint}/${id}/manuals/${manualId}`, manual);
  }

  public deleteManual(id: string, manualId: string): Observable<ApiResponse<Manual>> {
    return this.baseService.delete<Manual>(`${this.endpoint}/${id}/manuals/${manualId}`);
  }

  public updateManualAsset(id: string, manualId: string, assetId: string, file: File): Observable<ApiResponse<Manual>> {
    let formData: FormData = new FormData();
    formData.append('file', file, file.name);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');
    return this.baseService.add<Manual>(`${this.endpoint}/${id}/manuals/${manualId}/assets/${assetId}`, formData, headers);
  }

  public getTechnicalSheet(id: string): Observable<ApiResponse<List<TechnicalSheetEntry>>> {
    let observable = this.baseService.get<Product>(`${this.endpoint}/${id}`);
    return observable.pipe(map(data => {
      let technicalSheets = data.payload.technicalSheets || [];
      return <ApiResponse<List<TechnicalSheetEntry>>>{ code: data.code, message: data.message, errors: data.errors, payload: { elements: technicalSheets } };
    }));
  }

  public updateTechnicalSheet(id: string, technicalSheet: TechnicalSheet): Observable<ApiResponse<List<TechnicalSheetEntry>>> {
    let observable = this.baseService.add<Product>(`${this.endpoint}/${id}/sheets`, { technicalSheets: technicalSheet.entries });
    return observable.pipe(map(data => {
      let technicalSheets = data.payload.technicalSheets;
      return <ApiResponse<List<TechnicalSheetEntry>>>{ code: data.code, message: data.message, errors: data.errors, payload: { elements: technicalSheets } };
    }));
  }

  public updateTechnicalSheetImage(id: string, file: File, lang: string, index: number): Observable<ApiResponse<Product>> {
    let formData: FormData = new FormData();
    formData.append('file', file, file.name);
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');
    return this.baseService.add<Product>(`${this.endpoint}/${id}/sheets/asset?lang=${lang}&index=${index}`, formData, headers);
  }

  public updateVideos(id: string, videos: VideoEntry[]): Observable<ApiResponse<List<VideoEntry>>> {
    let observable = this.baseService.add<Product>(`${this.endpoint}/${id}/videos`, { videos: videos });
    return observable.pipe(map(data => {
      let videos = data.payload.videos;
      return <ApiResponse<List<VideoEntry>>>{ code: data.code, message: data.message, errors: data.errors, payload: { elements: videos } };
    }));
  }
}
